import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDriftAlertsList } from "./use-drift-alerts-list";
import { DriftAlertRow } from "./DriftAlertRow";
import { DriftAlertsPagination } from "./DriftAlertsPagination";
import { ResolveDriftAlertDialog } from "./ResolveDriftAlertDialog";
import { DRIFT_ALERTS_PAGE_SIZE } from "./constants";
import type { DriftAlert } from "./types";

export function DriftAlertsPage() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [resolveTarget, setResolveTarget] = useState<DriftAlert | null>(null);

  const { data, isLoading, isFetching, isError } = useDriftAlertsList(
    DRIFT_ALERTS_PAGE_SIZE,
    offset,
  );
  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  // Resolving the last alert on a non-first page leaves it empty —
  // step back so the operator lands on a page with work on it.
  useEffect(() => {
    if (!isLoading && !isError && items.length === 0 && offset > 0) {
      setOffset((current) => Math.max(0, current - DRIFT_ALERTS_PAGE_SIZE));
    }
  }, [isLoading, isError, items.length, offset]);

  return (
    <div data-testid="drift-alerts-screen" className="space-y-6">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("subscriptions.driftAlerts.eyebrow")}</p>
        <h1 className="text-4xl">{t("subscriptions.driftAlerts.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("subscriptions.driftAlerts.subtitle")}
        </p>
      </header>

      {isLoading && (
        <p data-testid="drift-alerts-loading" className="font-sans text-sm text-muted-foreground">
          {t("subscriptions.driftAlerts.loading")}
        </p>
      )}

      {isError && (
        <div
          data-testid="drift-alerts-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("subscriptions.driftAlerts.error")}
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div
          data-testid="drift-alerts-empty"
          className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center"
        >
          <p className="font-sans text-sm font-medium text-foreground">
            {t("subscriptions.driftAlerts.emptyTitle")}
          </p>
          <p className="mt-1 font-sans text-sm font-normal text-muted-foreground">
            {t("subscriptions.driftAlerts.emptyBody")}
          </p>
        </div>
      )}

      {!isError && items.length > 0 && (
        <div
          data-testid="drift-alerts-list"
          aria-busy={isFetching}
          className={isFetching ? "space-y-4 opacity-70 transition-opacity" : "space-y-4"}
        >
          <p className="font-sans text-sm font-normal text-muted-foreground">
            {t("subscriptions.driftAlerts.count", { count: totalCount })}
          </p>
          <div className="space-y-3">
            {items.map((alert) => (
              <DriftAlertRow key={alert.id} alert={alert} onResolve={setResolveTarget} />
            ))}
          </div>
          <DriftAlertsPagination
            totalCount={totalCount}
            limit={DRIFT_ALERTS_PAGE_SIZE}
            offset={offset}
            onOffsetChange={setOffset}
          />
        </div>
      )}

      <ResolveDriftAlertDialog
        alert={resolveTarget}
        open={resolveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setResolveTarget(null);
        }}
        onSuccess={() => setResolveTarget(null)}
      />
    </div>
  );
}
